import { Card } from "antd";
import {
  checkInQRTokenApi,
  getJobDriveInvitationsApi,
  getJobListApi,
  verifyQRTokenApi
} from "api/services/jobs";
import QRScanner from "components/Administration/Jobs/JobDrive/QRScanner";
import Breadcrumbs from "components/Breadcrumbs";
import { Button } from "components/Button";
import { useEffect,useMemo,useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import { Bounce,toast } from "react-toastify";
import { customStyleSelect } from "utils/common";

const JobDriveScan = () => {
  const { user, userLoading } = useSelector((state) => state.user);
  
  // Check if user has job_drive_scan_invitation permission
  const hasJobDrivePermission = useMemo(() => {
    // Wait until user is fully loaded
    if (userLoading) return null; // null means "still checking"
    // Check if user object is empty (initial state is {})
    if (!user || Object.keys(user).length === 0 || !user.id) return null; // Still waiting for user data
    // If user has no role or modules, they don't have permission
    if (!user?.role?.permissions && !user?.modules) return false;
    // Check for job_drive_scan_invitation permission
    return user.role?.permissions?.some((p) => p.code === "job_drive_scan_invitation") ||
           user?.modules?.some?.((m) => m.code === "job_drive_scan_invitation");
  }, [user, userLoading]);

  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  useEffect(() => {
    if (hasJobDrivePermission === true) {
      fetchJobs();
    }
  }, [hasJobDrivePermission]);

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const response = await getJobListApi({ job_drive_enabled: true });
      setJobs(response.data?.results || []);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to load jobs", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setJobsLoading(false);
    }
  };

  const handleManualVerify = async () => {
    if (!manualCode || !manualCode.trim()) {
      toast.error("Please enter an invitation code", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
      return;
    }

    setVerifyLoading(true);
    try {
      // Verify the token
      const verifyResponse = await verifyQRTokenApi(manualCode.trim());
      
      if (!verifyResponse.data.valid) {
        throw new Error(verifyResponse.data.error || "Invalid invitation code");
      }

      const invitation = verifyResponse.data.invitation;
      
      if (verifyResponse.data.already_checked_in) {
        toast.warning("This candidate has already checked in", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        return;
      }

      // If valid and not checked in, proceed with check-in
      await checkInQRTokenApi(manualCode.trim());
      
      toast.success("Check-in successful!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });

      // Clear the input
      setManualCode("");
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || error.message || "Invalid invitation code";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const params = {};
      
      if (selectedJob?.value) {
        params.job_id = selectedJob.value;
      }
      
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await getJobDriveInvitationsApi(params);
      
      // Handle different response structures
      const invitationsData = response.data;
      let invitationsArray = [];
      if (Array.isArray(invitationsData)) {
        invitationsArray = invitationsData;
      } else if (invitationsData?.results && Array.isArray(invitationsData.results)) {
        invitationsArray = invitationsData.results;
      } else if (invitationsData?.invitations && Array.isArray(invitationsData.invitations)) {
        invitationsArray = invitationsData.invitations;
      }
      
      setInvitations(invitationsArray);
      
      if (invitationsArray.length === 0) {
        toast.info("No invitations found", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      console.error("Error searching invitations:", error);
      toast.error("Failed to search invitations", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleScanSuccess = async (scanData) => {
    if (scanData?.token) {
      // The QRScanner already handles the check-in, so we just need to refresh if needed
      // The success is already shown in the scanner component
    }
  };

  // Wait for user to load before checking permissions
  if (userLoading || hasJobDrivePermission === null) {
    return <div>Loading...</div>;
  }

  // Redirect if no permission (after hooks)
  if (hasJobDrivePermission === false) {
    return <Navigate to="/manage-jobs" replace />;
  }

  // Prepare job options
  const jobOptions = jobs?.map(job => ({
    label: job.title,
    value: job.id
  })) || [];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle="Scan Invitation"
          title="Scan"
          subtitle="Job Drive"
          hideGrid={true}
        />
        
        <div className="row">
          {/* Scan QR Code Card */}
          <div className="col-12 mb-4">
            <Card
              style={{ borderRadius: "12px", border: "1px solid #e0e0e0" }}
              bodyStyle={{ padding: "20px", textAlign: "center" }}
              className="p-md-4"
            >
              <Button
                className="btn btn-primary btn-lg w-100 w-md-auto"
                icon="fa fa-qrcode"
                label="Scan QR Code"
                onClick={() => setQrScannerOpen(true)}
                style={{ minWidth: "200px", maxWidth: "100%" }}
              />
            </Card>
          </div>

          {/* Enter Manually Card */}
          <div className="col-12 mb-4">
            <Card
              title="Enter Manually"
              style={{ borderRadius: "12px", border: "1px solid #e0e0e0" }}
            >
              <div className="row">
                <div className="col-12">
                  <div className="input-block mb-3">
                    <label className="col-form-label">Invitation Code</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter invitation code"
                      value={manualCode}
                      onChange={(e) => setManualCode(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleManualVerify();
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="col-12 text-center">
                  <Button
                    className="btn btn-primary w-100 w-md-auto"
                    label="Verify Invitation"
                    onClick={handleManualVerify}
                    loading={verifyLoading}
                    disabled={!manualCode || manualCode.trim().length !== 6}
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* Search Invitation Card */}
          <div className="col-12">
            <Card
              title={
                <div className="d-flex justify-content-between align-items-center">
                  <span>Search Invitation</span>
                  <button
                    className="btn btn-link p-0"
                    onClick={() => setSearchExpanded(!searchExpanded)}
                    style={{ border: "none", background: "none" }}
                  >
                    <i className={`fa fa-chevron-${searchExpanded ? "up" : "down"}`}></i>
                  </button>
                </div>
              }
              style={{ borderRadius: "12px", border: "1px solid #e0e0e0" }}
            >
              {searchExpanded && (
                <div className="row">
                  <div className="col-12 col-md-6 mb-3">
                    <div className="input-block">
                      <label className="col-form-label">Job</label>
                      <Select
                        placeholder="Select an event"
                        value={selectedJob}
                        onChange={(option) => setSelectedJob(option)}
                        options={jobOptions}
                        styles={customStyleSelect}
                        isClearable
                        isLoading={jobsLoading}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6 mb-3">
                    <div className="input-block">
                      <label className="col-form-label">Search</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name or code"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            handleSearch();
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-12 mb-3 text-center">
                    <Button
                      className="btn btn-primary w-100 w-md-auto"
                      label="Search"
                      onClick={handleSearch}
                      loading={searchLoading}
                    />
                  </div>
                  {invitations.length > 0 && (
                    <div className="col-12">
                      <div className="table-responsive" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Time Slot</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invitations.map((invitation) => (
                              <tr key={invitation.id}>
                                <td>
                                  {invitation.applicant?.first_name} {invitation.applicant?.last_name}
                                </td>
                                <td>{invitation.applicant?.email || invitation.email_to}</td>
                                <td>{invitation.slot?.title || "Not Assigned"}</td>
                                <td>
                                  <span className={`badge badge-soft-${invitation.status === "sent" ? "success" : "warning"}`}>
                                    {invitation.status === "sent" ? "Sent" : "Pending"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {invitations.length === 0 && searchLoading === false && (selectedJob || searchTerm) && (
                    <div className="col-12">
                      <p className="text-muted text-center">No invitations found</p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* QR Scanner Modal */}
        <QRScanner
          open={qrScannerOpen}
          onCancel={() => setQrScannerOpen(false)}
          onScanSuccess={handleScanSuccess}
        />
      </div>
      <style>{`
        @media (max-width: 767.98px) {
          .w-md-auto {
            width: 100% !important;
          }
          .p-md-4 .ant-card-body {
            padding: 20px !important;
          }
        }
        @media (min-width: 768px) {
          .w-md-auto {
            width: auto !important;
          }
          .p-md-4 .ant-card-body {
            padding: 40px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default JobDriveScan;
